import { useState, useEffect } from 'react'
import Router, { useRouter } from 'next/router'
import Script from 'next/script'
import { ConfigProvider } from 'antd'
import TagManager from 'react-gtm-module'
import { appWithTranslation } from 'next-i18next'
import { Provider } from 'react-redux'
import { wrapper } from '../redux/store'
import { rehydrateAuth } from '@/features/auth/apis/authSlice'
import Loading from '../components/shared-components/Loading'
import ErrorBoundary from '../features/errorHandling/components/ErrorBoundary'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import itIT from 'antd/locale/it_IT'
import 'dayjs/locale/it'

// CSS FILES
import '../styles/globals.css'
import '../styles/formInputs.css'
import '../styles/layout.css'
import '../styles/tableFilters.css'

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)

export function App({ Component, ...rest }) {
  const { store, props } = wrapper.useWrappedStore(rest)
  const router = useRouter()

  const Layout = Component.Layout || (({ children }) => <>{children}</>)

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const handleStart = () => setLoading(true)
    const handleComplete = () => setLoading(false)

    Router.events.on('routeChangeStart', handleStart)
    Router.events.on('routeChangeComplete', handleComplete)
    Router.events.on('routeChangeError', handleComplete)

    return () => {
      Router.events.off('routeChangeStart', handleStart)
      Router.events.off('routeChangeComplete', handleComplete)
      Router.events.off('routeChangeError', handleComplete)
    }
  }, [])

  useEffect(() => {
    // Google Tag Manager for production only
    if (
      typeof window !== 'undefined' &&
      process.env.NEXT_PUBLIC_ENVIRONMET === 'production'
    ) {
      TagManager.initialize({
        gtmId: process.env.NEXT_PUBLIC_GTM_ID,
        dataLayerName: 'PageDataLayer',
      })
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedAuth = localStorage.getItem('SMARTCAF-AUTH-USER')
      if (savedAuth) {
        try {
          store.dispatch(rehydrateAuth(JSON.parse(savedAuth)))
        } catch {
          localStorage.removeItem('SMARTCAF-AUTH-USER')
        }
      }
    }
  }, [store])

  return (
    <>
      {/* Hotjar Script */}
      {process.env.NEXT_PUBLIC_ENVIRONMET === 'production' && (
        <Script
          id="hotjar-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (function(h,o,t,j,a,r){
                  h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                  h._hjSettings={hjid:3629008,hjsv:6};
                  a=o.getElementsByTagName('head')[0];
                  r=o.createElement('script');r.async=1;
                  r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                  a.appendChild(r);
              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `,
          }}
        />
      )}

      <ConfigProvider
        locale={itIT}
        theme={{
          token: {
            colorPrimary: '#8FC045',
          },
        }}
      >
        <Provider store={store}>
          <Elements stripe={stripePromise}>
            <Layout>
              <ErrorBoundary key={router.asPath}>
                {loading ? <Loading /> : <Component {...props.pageProps} />}
              </ErrorBoundary>
            </Layout>
          </Elements>
        </Provider>
      </ConfigProvider>
    </>
  )
}

export default appWithTranslation(wrapper.withRedux(App))
